import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  Check,
  ChevronDown,
  Database,
  FileText,
  Loader,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Shield,
  Wrench,
  X,
} from 'lucide-react';
import {
  CAPABILITY_ICON_OPTIONS,
  DEFAULT_CAPABILITY_ICON,
  DATA_MODEL_OPERATION_OPTIONS,
  MCP_CAPABILITY_OPTIONS,
  RESOURCE_SOURCE_OPTIONS,
  RISK_OPTIONS,
  TOOL_SOURCE_OPTIONS,
  resolveCapabilityIconKey,
  SINGLETON_ENDPOINT_ID,
  normalizeMcpToolName,
} from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import { capabilityGroupIds, createId } from '../governedMcpStore';
import { publishCatalogService } from '../publishCatalogService';
import {
  backingLabel,
  buildDataModelSchemas,
  buildKnowledgeBaseSchemas,
  buildWorkflowSchemas,
  deriveCapabilityKind,
  prettyJson,
} from '../publishSchemaBuilder';
import type {
  DataModelOperation,
  DataModelSource,
  ExternalMcpConnection,
  KnowledgeBaseSource,
  McpCapabilityKind,
  McpClient,
  PublishedCapability,
  PublishSourceKind,
  ToolGroup,
  WorkflowSource,
} from '../governedMcpTypes';
import {
  GatewayDrawer,
  MaximizableTextarea,
  ToggleRow,
  fieldClass,
  ghostBtn,
  labelClass,
  primaryBtn,
} from '../components/GatewayUi';
import IngestionEntityModal, { type IngestionEntityPayload } from '../../configurations/ingestion/popups/IngestionEntityModal';

function DataModelCatalogActions({
  isLight,
  refreshing,
  onAdd,
  onRefresh,
}: {
  isLight: boolean;
  refreshing: boolean;
  onAdd: () => void;
  onRefresh: () => void;
}) {
  const idle = isLight
    ? 'text-neutral-400 hover:text-indigo-600 hover:bg-indigo-50'
    : 'text-neutral-500 hover:text-indigo-400 hover:bg-neutral-800';
  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        title="Add data model"
        onClick={onAdd}
        className={`p-1 rounded-md transition-colors ${idle}`}
      >
        <Plus size={14} />
      </button>
      <button
        type="button"
        title="Refresh data models"
        onClick={onRefresh}
        disabled={refreshing}
        className={`p-1 rounded-md transition-colors disabled:opacity-50 disabled:pointer-events-none ${idle}`}
      >
        <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
      </button>
    </div>
  );
}

function CapabilityIconPicker({
  value,
  onChange,
  isLight,
}: {
  value?: string;
  onChange: (key: string) => void;
  isLight: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedKey = resolveCapabilityIconKey(value);
  const selected = CAPABILITY_ICON_OPTIONS.find((option) => option.key === selectedKey) || CAPABILITY_ICON_OPTIONS[0];
  const SelectedIcon = selected.icon;

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <label className={labelClass(isLight)}>Icon</label>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`${fieldClass(isLight)} flex items-center justify-between gap-2 text-left`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2 min-w-0">
          <span
            className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${
              isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'
            }`}
          >
            <SelectedIcon size={15} />
          </span>
          <span className="truncate">{selected.label}</span>
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}
        />
      </button>
      {open ? (
        <div
          className={`absolute z-30 mt-1.5 w-full max-h-64 overflow-y-auto rounded-xl border shadow-xl ${
            isLight ? 'border-neutral-200 bg-white' : 'border-neutral-700 bg-neutral-900'
          }`}
          role="listbox"
        >
          {CAPABILITY_ICON_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = option.key === selectedKey;
            return (
              <button
                key={option.key}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(option.key);
                  setOpen(false);
                }}
                className={`w-full px-3 py-2.5 flex items-center justify-between gap-2 text-left transition-colors ${
                  isLight ? 'hover:bg-neutral-50 text-neutral-800' : 'hover:bg-neutral-800 text-neutral-100'
                } ${isSelected ? (isLight ? 'bg-indigo-50/70' : 'bg-indigo-500/10') : ''}`}
              >
                <span className="inline-flex items-center gap-2 text-sm font-medium">
                  <Icon size={15} className={isLight ? 'text-neutral-600' : 'text-neutral-300'} />
                  {option.label}
                </span>
                {isSelected ? <Check size={14} className="text-indigo-500 shrink-0" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function Chip({
  selected,
  label,
  hint,
  onClick,
  disabled,
  isLight,
}: {
  selected: boolean;
  label: string;
  hint?: string;
  onClick: () => void;
  disabled?: boolean;
  isLight: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`group text-left rounded-xl border px-3 py-2.5 transition-all ${
        disabled
          ? isLight
            ? 'border-neutral-200 bg-neutral-50 text-neutral-400 cursor-not-allowed'
            : 'border-neutral-800 bg-neutral-900 text-neutral-600 cursor-not-allowed'
          : selected
            ? isLight
              ? 'border-indigo-500/70 bg-indigo-50/80 shadow-[0_0_0_3px_rgba(99,102,241,0.12)]'
              : 'border-indigo-400/70 bg-indigo-500/15 shadow-[0_0_0_3px_rgba(99,102,241,0.18)]'
            : isLight
              ? 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50'
              : 'border-neutral-800 bg-neutral-900 hover:border-neutral-700 hover:bg-neutral-800/60'
      }`}
    >
      <span className="flex items-start gap-2.5">
        <span
          className={`mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center shrink-0 ${
            selected
              ? 'border-indigo-500 bg-indigo-600 text-white'
              : isLight
                ? 'border-neutral-300 bg-white'
                : 'border-neutral-600 bg-neutral-900'
          }`}
        >
          {selected ? <Check size={10} strokeWidth={3} /> : null}
        </span>
        <span className="min-w-0">
          <span className={`block text-sm font-medium leading-5 ${disabled ? '' : isLight ? 'text-neutral-900' : 'text-white'}`}>
            {label}
          </span>
          {hint ? <span className={`block text-[11px] mt-0.5 leading-4 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{hint}</span> : null}
        </span>
      </span>
    </button>
  );
}

function Section({
  kicker,
  title,
  hint,
  action,
  children,
  isLight,
}: {
  kicker?: string;
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  isLight: boolean;
}) {
  return (
    <section
      className={`rounded-2xl border px-5 py-5 sm:px-6 ${
        isLight
          ? 'bg-white border-neutral-200/90 shadow-[0_1px_2px_rgba(16,24,40,0.04)]'
          : 'bg-neutral-900/70 border-neutral-800'
      }`}
    >
      <header className="flex items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          {kicker ? (
            <p className={`text-[10px] font-semibold uppercase tracking-[0.16em] mb-1 ${isLight ? 'text-indigo-500' : 'text-indigo-300'}`}>
              {kicker}
            </p>
          ) : null}
          <h3 className={`text-[15px] font-semibold tracking-tight ${isLight ? 'text-neutral-900' : 'text-white'}`}>{title}</h3>
          {hint ? <p className={`text-sm mt-1 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{hint}</p> : null}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function CompactSwitch({
  checked,
  onChange,
  isLight,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  isLight: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`inline-flex items-center gap-2.5 rounded-full pl-1 pr-3 py-1 border transition-colors ${
        checked
          ? isLight
            ? 'border-emerald-200 bg-emerald-50'
            : 'border-emerald-500/30 bg-emerald-500/10'
          : isLight
            ? 'border-neutral-200 bg-neutral-50'
            : 'border-neutral-700 bg-neutral-900'
      }`}
    >
      <span
        className={`relative w-8 h-5 rounded-full shrink-0 transition-colors ${
          checked ? 'bg-emerald-500' : isLight ? 'bg-neutral-300' : 'bg-neutral-700'
        }`}
      >
        <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-3' : ''}`} />
      </span>
      <span className={`text-xs font-medium ${checked ? (isLight ? 'text-emerald-700' : 'text-emerald-300') : isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        {label}
      </span>
    </button>
  );
}

const TYPE_ICONS: Record<McpCapabilityKind, React.ComponentType<{ size?: number; className?: string }>> = {
  tool: Wrench,
  resource: FileText,
  prompt: MessageSquare,
};

function toggleList(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

function toggleOp(list: DataModelOperation[], value: DataModelOperation): DataModelOperation[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function MultiSelectDropdown({
  label,
  values,
  options,
  onChange,
  isLight,
  placeholder = 'Select…',
}: {
  label: string;
  values: string[];
  options: { id: string; label: string; hint?: string; locked?: boolean }[];
  onChange: (next: string[]) => void;
  isLight: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  const selected = options.filter((option) => values.includes(option.id));
  const filtered = options.filter((option) => {
    const haystack = `${option.label} ${option.hint || ''}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  return (
    <div ref={rootRef} className="relative">
      <p className={labelClass(isLight)}>{label}</p>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`${fieldClass(isLight)} min-h-[46px] h-auto py-1.5 flex items-center justify-between gap-2 text-left`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex flex-wrap gap-1.5 min-w-0 flex-1">
          {selected.length ? (
            selected.map((option) => (
              <span
                key={option.id}
                className={`inline-flex items-center gap-1 max-w-full rounded-full pl-2.5 pr-1 py-0.5 text-xs font-medium ${
                  isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-200'
                }`}
              >
                <span className="truncate">{option.label}</span>
                {option.locked ? null : (
                  <span
                    role="button"
                    tabIndex={0}
                    className={`rounded-full p-0.5 ${isLight ? 'hover:bg-indigo-100' : 'hover:bg-indigo-500/30'}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChange(toggleList(values, option.id));
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onChange(toggleList(values, option.id));
                      }
                    }}
                    aria-label={`Remove ${option.label}`}
                  >
                    <X size={11} />
                  </span>
                )}
              </span>
            ))
          ) : (
            <span className={isLight ? 'text-neutral-400' : 'text-neutral-500'}>{placeholder}</span>
          )}
        </span>
        <ChevronDown size={14} className={`shrink-0 ${open ? 'rotate-180' : ''} ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
      </button>
      {open ? (
        <div
          className={`absolute z-30 mt-1.5 w-full rounded-xl border shadow-xl overflow-hidden ${
            isLight ? 'bg-white border-neutral-200 shadow-neutral-900/10' : 'bg-neutral-900 border-neutral-700 shadow-black/40'
          }`}
        >
          <div className={`flex items-center gap-2 px-3 py-2.5 border-b ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
            <Search size={13} className={isLight ? 'text-neutral-400' : 'text-neutral-500'} />
            <input
              className={`w-full bg-transparent text-sm outline-none ${isLight ? 'text-neutral-900 placeholder:text-neutral-400' : 'text-white placeholder:text-neutral-500'}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search"
              autoFocus
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1" role="listbox">
            {filtered.length ? (
              filtered.map((option) => {
                const checked = values.includes(option.id);
                return (
                  <li key={option.id}>
                    <label
                      className={`flex items-start gap-2.5 px-3 py-2.5 text-sm cursor-pointer ${
                        isLight ? 'hover:bg-neutral-50 text-neutral-800' : 'hover:bg-neutral-800 text-neutral-200'
                      } ${option.locked ? 'opacity-80' : ''}`}
                    >
                      <span
                        className={`mt-0.5 h-4 w-4 rounded-md border flex items-center justify-center shrink-0 ${
                          checked
                            ? 'border-indigo-500 bg-indigo-600 text-white'
                            : isLight
                              ? 'border-neutral-300 bg-white'
                              : 'border-neutral-600 bg-neutral-900'
                        }`}
                      >
                        {checked ? <Check size={10} strokeWidth={3} /> : null}
                      </span>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        disabled={option.locked && checked}
                        onChange={() => {
                          if (option.locked && checked) return;
                          onChange(toggleList(values, option.id));
                        }}
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{option.label}</span>
                        {option.hint ? (
                          <span className={`block text-[11px] ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>{option.hint}</span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })
            ) : (
              <li className={`px-3 py-2.5 text-sm ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>No matches</li>
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function blankPublished(endpointId: string, groupIds: string[] = [], capability: McpCapabilityKind = 'tool'): PublishedCapability {
  const assigned = groupIds.filter(Boolean);
  return {
    id: createId('pub'),
    capabilityKind: 'information',
    backedBy: 'data_model',
    sourceId: '',
    sourceName: '',
    mcpRepresentations: capability === 'resource' ? ['resource'] : ['tool'],
    mcpToolName: '',
    icon: DEFAULT_CAPABILITY_ICON,
    mcpResourceUri: '',
    mcpTaskName: '',
    description: '',
    inputSchema: '{ }',
    outputSchema: '{ }',
    useWhen: '',
    doNotUseWhen: '',
    usageGuidance: '',
    endpointId,
    allowedClientIds: [],
    allowedUserIds: [],
    allowedGroupIds: [],
    environment: 'development',
    riskLevel: 'medium',
    dataClassification: 'internal',
    rateLimitPerMinute: 20,
    maxRecords: 25,
    requiresApproval: false,
    outputGovernance: true,
    maskingEnabled: true,
    promptInjectionProtection: true,
    piiDetection: true,
    secretsLeakageProtection: true,
    jailbreakProtection: true,
    version: '1.0.0',
    status: 'published',
    readOnly: true,
    destructive: false,
    hasSideEffects: false,
    isIdempotent: true,
    requiresConfirmation: false,
    selectedExistingToolNames: [],
    selectedExistingResourceUris: [],
    groupId: assigned[0] || '',
    groupIds: assigned,
    resourceSchema: '',
    mcpCapability: capability,
    sourceKind: 'data_model',
    dataModelOperations: [],
    selectedTableIds: [],
    customResourceText: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function sourceKindFromBackedBy(backedBy: PublishedCapability['backedBy']): PublishSourceKind {
  if (backedBy === 'external_mcp') return 'external_mcp';
  if (backedBy === 'knowledge_base') return 'knowledge_base';
  if (backedBy === 'workflow' || backedBy === 'long_running_workflow') return 'workflow';
  return 'data_model';
}

function mergeGroupIds(primaryId: string, extraIds: string[]): string[] {
  const extras = extraIds.filter((id) => id && id !== primaryId);
  return primaryId ? [primaryId, ...extras] : extras;
}

export default function PublishCapabilityDrawer({
  open,
  initial,
  clients,
  dataModels,
  workflows,
  externalMcp,
  knowledgeBases = [],
  toolGroups,
  defaultGroupId,
  defaultCapability,
  catalogLoading,
  catalogError,
  onRetryCatalogs,
  isLight,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: PublishedCapability | null;
  clients: McpClient[];
  dataModels: DataModelSource[];
  workflows: WorkflowSource[];
  externalMcp: ExternalMcpConnection[];
  knowledgeBases?: KnowledgeBaseSource[];
  toolGroups: ToolGroup[];
  defaultGroupId?: string;
  defaultCapability?: McpCapabilityKind;
  catalogLoading?: boolean;
  catalogError?: string | null;
  onRetryCatalogs?: () => void;
  isLight: boolean;
  onClose: () => void;
  onSave: (item: PublishedCapability) => void;
}) {
  const [form, setForm] = useState<PublishedCapability>(blankPublished(SINGLETON_ENDPOINT_ID, defaultGroupId ? [defaultGroupId] : []));
  const [error, setError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [resolvedModel, setResolvedModel] = useState<DataModelSource | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [catalogModels, setCatalogModels] = useState<DataModelSource[]>(dataModels);
  const [refreshingModels, setRefreshingModels] = useState(false);
  const [showIngestionModal, setShowIngestionModal] = useState(false);
  const [creatingModel, setCreatingModel] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const lastSchemaKey = useRef('');
  const skipNextSchemaApply = useRef(true);

  useEffect(() => {
    setCatalogModels(dataModels);
  }, [dataModels]);

  useEffect(() => {
    if (!open) return;
    const assigned = initial ? capabilityGroupIds(initial) : defaultGroupId ? [defaultGroupId] : [];
    const next = initial
      ? {
          ...blankPublished(SINGLETON_ENDPOINT_ID, assigned),
          ...initial,
          mcpCapability:
            initial.mcpCapability ||
            (initial.mcpRepresentations?.includes('resource') && !initial.mcpRepresentations?.includes('tool') ? 'resource' : 'tool'),
          sourceKind: initial.sourceKind || sourceKindFromBackedBy(initial.backedBy),
          dataModelOperations: initial.dataModelOperations || [],
          selectedTableIds: initial.selectedTableIds || [],
          customResourceText: initial.customResourceText || '',
          groupIds: assigned,
          groupId: assigned[0] || '',
          endpointId: SINGLETON_ENDPOINT_ID,
          status: initial.status === 'disabled' ? ('disabled' as const) : ('published' as const),
        }
      : blankPublished(SINGLETON_ENDPOINT_ID, assigned, defaultCapability || 'tool');
    setForm({ ...next, icon: resolveCapabilityIconKey(next.icon) });
    setError(null);
    setAdvancedOpen(false);
    setResolvedModel(null);
    setDetailError(null);
    setShowIngestionModal(false);
    setCreateError(null);
    lastSchemaKey.current = '';
    skipNextSchemaApply.current = true;
    logGovernedMcp('app', 'publish_popup_open', { edit: Boolean(initial), groupIds: next.groupIds, capability: next.mcpCapability });
  }, [open, initial, defaultGroupId, defaultCapability]);

  useEffect(() => {
    if (!open || form.sourceKind !== 'data_model' || !form.sourceId) {
      setResolvedModel(null);
      return;
    }
    const listed = catalogModels.find((item) => item.id === form.sourceId) || null;
    let cancelled = false;
    const load = async () => {
      setDetailLoading(true);
      setDetailError(null);
      try {
        const detail = await publishCatalogService.getDataModelDetail(form.sourceId);
        if (cancelled) return;
        const base = listed || detail;
        if (!base) {
          setResolvedModel(null);
          setDetailError('Data model was not found.');
          return;
        }
        const merged: DataModelSource = {
          ...base,
          ...(detail || {}),
          id: detail?.id || base.id,
          name: detail?.name || base.name,
          code: detail?.code || base.code,
          description: detail?.description || base.description,
          classification: detail?.classification || base.classification,
          fields: detail?.fields?.length ? detail.fields : base.fields,
          tables: detail?.tables?.length ? detail.tables : base.tables || [],
          backing: detail?.backing || base.backing,
        };
        setResolvedModel(merged);
        logGovernedMcp('app', 'publish_data_model_resolved', {
          id: merged.id,
          backing: merged.backing,
          tables: merged.tables?.length || 0,
        });
      } catch (err) {
        if (cancelled) return;
        if (listed?.tables?.length) {
          setResolvedModel(listed);
          setDetailError(null);
          logGovernedMcp('warning', 'publish_data_model_detail_fallback', { id: listed.id });
        } else {
          setResolvedModel(listed);
          setDetailError(err instanceof Error ? err.message : 'Could not load data model tables');
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, form.sourceId, form.sourceKind, catalogModels]);

  const selectedWorkflow = workflows.find((item) => item.id === form.sourceId);
  const selectedExternal = externalMcp.find((item) => item.id === form.sourceId);
  const activeModel = resolvedModel || catalogModels.find((item) => item.id === form.sourceId) || null;
  const tables = activeModel?.tables || [];
  const tableIds = tables.map((table) => table.id);
  const selectedTableCount = tableIds.filter((id) => form.selectedTableIds.includes(id)).length;
  const allTablesSelected = tableIds.length > 0 && selectedTableCount === tableIds.length;
  const sourceOptions = form.mcpCapability === 'resource' ? RESOURCE_SOURCE_OPTIONS : TOOL_SOURCE_OPTIONS;

  const toggleSelectAllTables = () => {
    setForm((prev) => {
      const nextIds = allTablesSelected ? [] : tableIds;
      logGovernedMcp('app', 'publish_tables_select_all', {
        selected: !allTablesSelected,
        count: nextIds.length,
      });
      return { ...prev, selectedTableIds: nextIds };
    });
  };
  const isActive = form.status !== 'disabled';
  const primaryGroupId = form.groupId || capabilityGroupIds(form)[0] || '';

  const applySchemas = (next: PublishedCapability): PublishedCapability => {
    const model = resolvedModel || catalogModels.find((item) => item.id === next.sourceId) || activeModel;
    const modelTables = model?.tables || [];
    const chosenTables = modelTables.filter((table) => next.selectedTableIds.includes(table.id));
    const workflow = workflows.find((item) => item.id === next.sourceId);
    const kb = knowledgeBases.find((item) => item.id === next.sourceId);
    const longRunning = Boolean(workflow?.longRunning);
    const capabilityKind = deriveCapabilityKind({
      mcpCapability: next.mcpCapability,
      sourceKind: next.sourceKind,
      operations: next.dataModelOperations,
      longRunning,
    });
    const representations: PublishedCapability['mcpRepresentations'] =
      next.mcpCapability === 'resource' ? ['resource'] : capabilityKind === 'process' ? ['tool', 'task'] : ['tool'];
    let patched: PublishedCapability = {
      ...next,
      capabilityKind,
      mcpRepresentations: representations,
      backedBy:
        next.sourceKind === 'external_mcp'
          ? 'external_mcp'
          : next.sourceKind === 'knowledge_base'
            ? 'knowledge_base'
            : next.sourceKind === 'workflow'
              ? longRunning
                ? 'long_running_workflow'
                : 'workflow'
              : 'data_model',
      readOnly: !next.dataModelOperations.some((op) => op === 'create' || op === 'update' || op === 'delete'),
      destructive: next.dataModelOperations.includes('delete'),
      hasSideEffects:
        next.dataModelOperations.some((op) => op === 'create' || op === 'update' || op === 'delete') || next.sourceKind === 'workflow',
    };

    if (next.sourceKind === 'data_model' && model) {
      const schemas = buildDataModelSchemas({
        model,
        tables: chosenTables.length ? chosenTables : modelTables,
        operations: next.dataModelOperations,
        capability: next.mcpCapability,
      });
      patched = {
        ...patched,
        inputSchema: schemas.inputSchema,
        outputSchema: schemas.outputSchema,
        resourceSchema: schemas.resourceSchema,
        mcpResourceUri: `selfic://models/${model.code}/${(chosenTables[0] || modelTables[0])?.id || 'records'}`,
      };
    } else if (next.sourceKind === 'workflow') {
      const schemas = buildWorkflowSchemas(longRunning);
      patched = {
        ...patched,
        inputSchema: schemas.inputSchema,
        outputSchema: schemas.outputSchema,
        mcpTaskName: longRunning ? `${workflow?.code || 'task'}_task` : '',
      };
    } else if (next.sourceKind === 'knowledge_base' && kb) {
      const schemas = buildKnowledgeBaseSchemas(kb.name);
      patched = { ...patched, inputSchema: schemas.inputSchema, outputSchema: schemas.outputSchema };
    } else if (next.sourceKind === 'custom_text') {
      patched = {
        ...patched,
        resourceSchema: next.customResourceText,
        mcpResourceUri: next.mcpResourceUri || `selfic://custom/${next.mcpToolName || 'resource'}`,
      };
    }
    return patched;
  };

  const schemaSourceKey = useMemo(
    () =>
      [
        form.mcpCapability,
        form.sourceKind,
        form.sourceId,
        form.selectedTableIds.join(','),
        form.dataModelOperations.join(','),
        form.customResourceText,
        form.selectedExistingToolNames.join(','),
        resolvedModel?.id || '',
      ].join('|'),
    [
      form.mcpCapability,
      form.sourceKind,
      form.sourceId,
      form.selectedTableIds,
      form.dataModelOperations,
      form.customResourceText,
      form.selectedExistingToolNames,
      resolvedModel?.id,
    ]
  );

  useEffect(() => {
    if (!open) return;
    const sourcePending =
      form.sourceKind === 'data_model' &&
      Boolean(form.sourceId) &&
      (detailLoading || resolvedModel?.id !== form.sourceId);
    if (sourcePending) return;
    if (skipNextSchemaApply.current) {
      skipNextSchemaApply.current = false;
      lastSchemaKey.current = schemaSourceKey;
      return;
    }
    if (lastSchemaKey.current === schemaSourceKey) return;
    lastSchemaKey.current = schemaSourceKey;
    setForm((prev) => applySchemas(prev));
    logGovernedMcp('app', 'publish_schemas_applied', {
      sourceKind: form.sourceKind,
      tables: form.selectedTableIds,
      operations: form.dataModelOperations,
    });
  }, [
    open,
    schemaSourceKey,
    detailLoading,
    resolvedModel?.id,
    form.sourceKind,
    form.sourceId,
    form.selectedTableIds,
    form.dataModelOperations,
  ]);

  const applyCapability = (capability: McpCapabilityKind) => {
    if (capability === 'prompt') {
      logGovernedMcp('warning', 'publish_capability_prompt_blocked', {});
      return;
    }
    const sourceKind: PublishSourceKind =
      capability === 'resource' ? 'data_model' : form.sourceKind === 'custom_text' ? 'data_model' : form.sourceKind;
    setForm((prev) => ({
      ...prev,
      mcpCapability: capability,
      sourceKind,
      sourceId: sourceKind === prev.sourceKind ? prev.sourceId : '',
      sourceName: sourceKind === prev.sourceKind ? prev.sourceName : '',
      mcpRepresentations: capability === 'resource' ? ['resource'] : ['tool'],
    }));
    setError(null);
    logGovernedMcp('app', 'publish_capability_selected', { capability });
  };

  const applySourceKind = (sourceKind: PublishSourceKind) => {
    setForm((prev) => ({
      ...prev,
      sourceKind,
      sourceId: '',
      sourceName: '',
      selectedTableIds: [],
      dataModelOperations: [],
      selectedExistingToolNames: [],
      selectedExistingResourceUris: [],
      customResourceText: sourceKind === 'custom_text' ? prev.customResourceText : '',
    }));
    setResolvedModel(null);
    setError(null);
    logGovernedMcp('app', 'publish_source_kind_selected', { sourceKind });
  };

  const applyListedSource = (sourceId: string, sourceName: string) => {
    setForm((prev) => ({
      ...prev,
      sourceId,
      sourceName,
      selectedTableIds: [],
      dataModelOperations: prev.sourceKind === 'data_model' ? prev.dataModelOperations : [],
    }));
    setError(null);
    logGovernedMcp('app', 'publish_source_selected', { sourceId, sourceKind: form.sourceKind });
  };

  const refreshDataModels = useCallback(async (): Promise<DataModelSource[]> => {
    setRefreshingModels(true);
    logGovernedMcp('app', 'publish_data_models_refresh');
    onRetryCatalogs?.();
    try {
      const rows = await publishCatalogService.listDataModels();
      setCatalogModels(rows);
      logGovernedMcp('app', 'publish_data_models_refresh_ok', { count: rows.length });
      return rows;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to refresh data models';
      logGovernedMcp('error', 'publish_data_models_refresh_failed', { message });
      return [];
    } finally {
      setRefreshingModels(false);
    }
  }, [onRetryCatalogs]);

  const handleOpenIngestionModal = () => {
    if (form.sourceKind !== 'data_model') applySourceKind('data_model');
    setCreateError(null);
    setShowIngestionModal(true);
    logGovernedMcp('app', 'publish_add_data_model_opened');
  };

  const handleCreateDataModel = async (payload: IngestionEntityPayload) => {
    setCreatingModel(true);
    setCreateError(null);
    try {
      const created = await publishCatalogService.createDataModel(payload);
      setShowIngestionModal(false);
      const rows = await refreshDataModels();
      const selected = created.id
        ? rows.find((item) => item.id === created.id)
        : rows.find((item) => item.name === created.name);
      const sourceId = selected?.id || created.id;
      const sourceName = selected?.name || created.name;
      if (sourceId) {
        applyListedSource(sourceId, sourceName);
      }
      logGovernedMcp('app', 'publish_add_data_model_saved', { sourceId, sourceName });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create data model.';
      setCreateError(message);
    } finally {
      setCreatingModel(false);
    }
  };

  const setPrimaryGroup = (groupId: string) => {
    setForm((prev) => {
      const groupIds = mergeGroupIds(groupId, capabilityGroupIds(prev));
      logGovernedMcp('app', 'publish_identity_group_changed', { groupId, groupIds });
      return { ...prev, groupId, groupIds };
    });
  };

  const collectErrors = (): string[] => {
    const issues: string[] = [];
    if (!normalizeMcpToolName(form.mcpToolName.trim()).replace(/_+/g, '')) issues.push('MCP tool name is required.');
    if (!form.description.trim()) issues.push('Description is required.');
    if (!capabilityGroupIds(form).length) issues.push('Assign the capability to a group.');
    if (form.mcpCapability === 'prompt') issues.push('Prompt capabilities are not available yet.');
    if (form.sourceKind === 'custom_text') {
      if (!form.customResourceText.trim()) issues.push('Custom resource text is required.');
    } else if (!form.sourceId) {
      issues.push('Select a source for this capability.');
    }
    if (form.sourceKind === 'data_model') {
      if (!form.dataModelOperations.length) issues.push('Choose at least one data model operation.');
      if (tables.length && !form.selectedTableIds.length) issues.push('Select at least one table, file, or entity.');
    }
    if (form.mcpCapability === 'tool') {
      if (!prettyJson(form.inputSchema) && form.inputSchema.replace(/\s+/g, '') !== '{}') issues.push('Input schema must be valid JSON.');
      if (!prettyJson(form.outputSchema) && form.outputSchema.replace(/\s+/g, '') !== '{}') issues.push('Output schema must be valid JSON.');
    }
    return issues;
  };

  const submit = () => {
    const issues = collectErrors();
    if (issues.length) {
      setError(issues[0]);
      logGovernedMcp('warning', 'publish_submit_blocked', { issues });
      return;
    }
    const next = applySchemas({
      ...form,
      mcpToolName: normalizeMcpToolName(form.mcpToolName.trim()),
      version: form.version.trim() || '1.0.0',
      status: form.status === 'disabled' ? 'disabled' : 'published',
    });
    const groupIds = mergeGroupIds(next.groupId || capabilityGroupIds(next)[0] || '', capabilityGroupIds(next));
    logGovernedMcp('app', 'publish_submit', {
      capability: next.mcpCapability,
      sourceKind: next.sourceKind,
      sourceId: next.sourceId,
      groupIds,
      status: next.status,
    });
    onSave({ ...next, groupIds, groupId: groupIds[0] || '' });
  };

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const title = isLight ? 'text-neutral-900' : 'text-white';
  const sourceLabel = sourceOptions.find((option) => option.value === form.sourceKind)?.label || 'Source';
  const well = isLight ? 'rounded-xl border border-neutral-200/80 bg-neutral-50/80 p-4 space-y-4' : 'rounded-xl border border-neutral-800 bg-neutral-950/50 p-4 space-y-4';

  return (
    <>
    <GatewayDrawer
      open={open}
      eyebrow="Governed MCP"
      icon={<Database size={18} className="text-indigo-500" />}
      title={initial ? form.mcpToolName || 'Edit capability' : 'New capability'}
      subtitle="Name the contract, bind a governed source, then publish. Clients request — Selfic executes."
      status={{ label: isActive ? 'Active' : 'Inactive', tone: isActive ? 'success' : 'neutral' }}
      isLight={isLight}
      onClose={onClose}
      size="full"
      footer={
        <>
          <span className={`mr-auto hidden sm:flex items-center gap-2 text-xs ${muted}`}>
            <span className={`capitalize ${title}`}>{form.mcpCapability}</span>
            <span className={isLight ? 'text-neutral-300' : 'text-neutral-700'}>·</span>
            <span>{sourceLabel}</span>
            <span className={isLight ? 'text-neutral-300' : 'text-neutral-700'}>·</span>
            <span>
              {form.allowedClientIds.length
                ? `${form.allowedClientIds.length} client${form.allowedClientIds.length === 1 ? '' : 's'}`
                : 'No clients'}
            </span>
          </span>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primaryBtn()} onClick={submit}>
            {initial ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      <div className="max-w-4xl mx-auto space-y-5 pb-2">
        {error ? (
          <div className="flex items-start gap-2.5 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-500">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            {error}
          </div>
        ) : null}

        <Section
          kicker="01"
          title="Identity"
          hint="How this capability appears to MCP clients."
          isLight={isLight}
          action={
            <CompactSwitch
              checked={isActive}
              label={isActive ? 'Active' : 'Inactive'}
              isLight={isLight}
              onChange={(next) => {
                const status = next ? 'published' : 'disabled';
                setForm((p) => ({ ...p, status }));
                logGovernedMcp('app', 'publish_active_toggled', { status });
              }}
            />
          }
        >
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass(isLight)}>Tool name</label>
              <input
                className={fieldClass(isLight)}
                value={form.mcpToolName}
                onChange={(e) => setForm((p) => ({ ...p, mcpToolName: normalizeMcpToolName(e.target.value) }))}
                placeholder="get_customer_exposure"
                onBlur={() => setForm((p) => ({ ...p, mcpToolName: normalizeMcpToolName(p.mcpToolName.trim()) }))}
              />
            </div>
            <CapabilityIconPicker
              isLight={isLight}
              value={form.icon}
              onChange={(icon) => {
                setForm((p) => ({ ...p, icon }));
                logGovernedMcp('app', 'publish_icon_selected', { icon });
              }}
            />
            <div>
              <label className={labelClass(isLight)}>Group</label>
              <select className={fieldClass(isLight)} value={primaryGroupId} onChange={(e) => setPrimaryGroup(e.target.value)}>
                <option value="">Select a group</option>
                {toolGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              {!toolGroups.length ? (
                <p className={`text-[11px] mt-1 ${muted}`}>Create a group on the Tools tab first.</p>
              ) : null}
            </div>
          </div>
          <div className="mt-4">
            <label className={labelClass(isLight)}>Description</label>
            <textarea
              className={fieldClass(isLight)}
              rows={2}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="What this capability does, in one or two sentences."
            />
          </div>
        </Section>

        <Section kicker="02" title="Audience" hint="Which AI clients may call this capability." isLight={isLight}>
          <div>
            <p className={labelClass(isLight)}>Type</p>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Capability type">
              {MCP_CAPABILITY_OPTIONS.map((option) => {
                const selected = form.mcpCapability === option.value;
                const Icon = TYPE_ICONS[option.value];
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={option.disabled}
                    onClick={() => applyCapability(option.value)}
                    className={`rounded-2xl border px-3 py-3 text-left transition-all ${
                      option.disabled
                        ? isLight
                          ? 'border-neutral-200 bg-neutral-50 text-neutral-400 cursor-not-allowed'
                          : 'border-neutral-800 bg-neutral-950 text-neutral-600 cursor-not-allowed'
                        : selected
                          ? isLight
                            ? 'border-indigo-500/70 bg-indigo-50/80 shadow-[0_0_0_3px_rgba(99,102,241,0.12)]'
                            : 'border-indigo-400/70 bg-indigo-500/15 shadow-[0_0_0_3px_rgba(99,102,241,0.18)]'
                          : isLight
                            ? 'border-neutral-200 bg-white hover:border-neutral-300'
                            : 'border-neutral-800 bg-neutral-950 hover:border-neutral-700'
                    }`}
                  >
                    <Icon size={16} className={selected ? 'text-indigo-600' : isLight ? 'text-neutral-400' : 'text-neutral-500'} />
                    <span className={`block text-sm font-semibold mt-2 ${option.disabled ? '' : isLight ? 'text-neutral-900' : 'text-white'}`}>
                      {option.label}
                      {option.disabled ? ' · soon' : ''}
                    </span>
                    <span className={`block text-[11px] mt-0.5 leading-4 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{option.hint}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-4">
            <MultiSelectDropdown
              label="Clients"
              values={form.allowedClientIds}
              options={clients.map((client) => ({
                id: client.id,
                label: client.name,
                hint: client.registrationId,
              }))}
              onChange={(allowedClientIds) => {
                setForm((p) => ({ ...p, allowedClientIds }));
                logGovernedMcp('app', 'publish_clients_changed', { allowedClientIds });
              }}
              isLight={isLight}
              placeholder="None selected"
            />
          </div>
        </Section>

        <Section kicker="03" title="Source" hint="What Selfic will execute when a client invokes this capability." isLight={isLight}>
          <p className={labelClass(isLight)}>Backed by</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {sourceOptions.map((option) => {
              const selected = form.sourceKind === option.value;
              const isDataModel = option.value === 'data_model';
              const cardClass = `rounded-xl border transition-all ${
                    selected
                      ? isLight
                        ? 'border-indigo-500/70 bg-indigo-50/80 shadow-[0_0_0_3px_rgba(99,102,241,0.12)]'
                        : 'border-indigo-400/70 bg-indigo-500/15 shadow-[0_0_0_3px_rgba(99,102,241,0.18)]'
                      : isLight
                        ? 'border-neutral-200 bg-white hover:border-neutral-300'
                        : 'border-neutral-800 bg-neutral-950 hover:border-neutral-700'
                  }`;
              const body = (
                <>
                  <span className={`block text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{option.label}</span>
                  <span className={`block text-[11px] mt-0.5 ${muted}`}>{option.hint}</span>
                </>
              );
              if (isDataModel) {
                return (
                  <div key={option.value} className={`relative ${cardClass}`}>
                    <button
                      type="button"
                      onClick={() => applySourceKind(option.value)}
                      className={`w-full text-left px-3.5 py-3 ${selected ? 'pr-16' : ''}`}
                    >
                      {body}
                    </button>
                    {selected ? (
                      <div className="absolute top-2 right-2">
                        <DataModelCatalogActions
                          isLight={isLight}
                          refreshing={refreshingModels || Boolean(catalogLoading)}
                          onAdd={handleOpenIngestionModal}
                          onRefresh={() => {
                            void refreshDataModels();
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              }
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => applySourceKind(option.value)}
                  className={`${cardClass} px-3.5 py-3 text-left`}
                >
                  {body}
                </button>
              );
            })}
          </div>

          {catalogLoading ? (
            <p className={`flex items-center gap-2 text-sm mt-4 ${muted}`}>
              <Loader size={14} className="animate-spin" /> Loading catalog…
            </p>
          ) : null}
          {catalogError ? (
            <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2.5 text-sm text-red-500">
              <span>{catalogError}</span>
              {onRetryCatalogs ? (
                <button type="button" className={ghostBtn(isLight)} onClick={onRetryCatalogs}>
                  Retry
                </button>
              ) : null}
            </div>
          ) : null}

          {form.sourceKind === 'data_model' ? (
            <div className={`${well} mt-4`}>
              <div>
                <div className="flex items-center justify-between gap-2">
                  <label className={labelClass(isLight)}>Data model</label>
                  <DataModelCatalogActions
                    isLight={isLight}
                    refreshing={refreshingModels || Boolean(catalogLoading)}
                    onAdd={handleOpenIngestionModal}
                    onRefresh={() => {
                      void refreshDataModels();
                    }}
                  />
                </div>
                <select
                  className={fieldClass(isLight)}
                  value={form.sourceId}
                  onChange={(e) => {
                    const model = catalogModels.find((item) => item.id === e.target.value);
                    applyListedSource(e.target.value, model?.name || '');
                  }}
                >
                  <option value="">Select a data model</option>
                  {catalogModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                      {model.backing ? ` · ${backingLabel(model.backing)}` : ''}
                    </option>
                  ))}
                  {form.sourceId && !catalogModels.some((model) => model.id === form.sourceId) ? (
                    <option value={form.sourceId}>{form.sourceName || 'Newly created model'}</option>
                  ) : null}
                </select>
              </div>
              {detailLoading ? (
                <p className={`flex items-center gap-2 text-sm ${muted}`}>
                  <Loader size={14} className="animate-spin" /> Loading tables and backing store…
                </p>
              ) : null}
              {detailError ? <p className="text-sm text-amber-500">{detailError}</p> : null}
              {activeModel ? (
                <p className={`text-xs ${muted}`}>
                  {backingLabel(activeModel.backing)} · {activeModel.description || activeModel.code}
                </p>
              ) : null}
              {activeModel ? (
                <div>
                  <p className={labelClass(isLight)}>Operations</p>
                  <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {DATA_MODEL_OPERATION_OPTIONS.map((option) => (
                      <Chip
                        key={option.value}
                        isLight={isLight}
                        selected={form.dataModelOperations.includes(option.value)}
                        label={option.label}
                        hint={option.hint}
                        onClick={() => setForm((prev) => ({ ...prev, dataModelOperations: toggleOp(prev.dataModelOperations, option.value) }))}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
              {tables.length ? (
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <p className={`${labelClass(isLight)} mb-0`}>
                      {activeModel?.backing === 'google_drive' ? 'Files / folders' : 'Tables'}
                    </p>
                    <button
                      type="button"
                      className={`text-xs font-medium mb-1.5 ${
                        isLight ? 'text-indigo-600 hover:text-indigo-700' : 'text-indigo-300 hover:text-indigo-200'
                      }`}
                      onClick={toggleSelectAllTables}
                    >
                      {allTablesSelected ? 'Clear all' : 'Select all'}
                      {selectedTableCount ? ` · ${selectedTableCount}` : ''}
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-2">
                    <Chip
                      isLight={isLight}
                      selected={allTablesSelected}
                      label="Select all"
                      hint={allTablesSelected ? `${tables.length} selected` : `${selectedTableCount} of ${tables.length}`}
                      onClick={toggleSelectAllTables}
                    />
                    {tables.map((table) => (
                      <Chip
                        key={table.id}
                        isLight={isLight}
                        selected={form.selectedTableIds.includes(table.id)}
                        label={table.name}
                        hint={`${table.kind || 'table'} · ${table.fields.length} fields`}
                        onClick={() =>
                          setForm((prev) => ({ ...prev, selectedTableIds: toggleList(prev.selectedTableIds, table.id) }))
                        }
                      />
                    ))}
                  </div>
                </div>
              ) : activeModel && !detailLoading ? (
                <p className={`text-sm ${muted}`}>No tables or files were discovered on this model yet.</p>
              ) : null}
            </div>
          ) : null}

          {form.sourceKind === 'workflow' ? (
            <div className={`${well} mt-4`}>
              <label className={labelClass(isLight)}>Workflow</label>
              <select
                className={fieldClass(isLight)}
                value={form.sourceId}
                onChange={(e) => {
                  const workflow = workflows.find((item) => item.id === e.target.value);
                  applyListedSource(e.target.value, workflow?.name || '');
                }}
              >
                <option value="">Select a workflow</option>
                {workflows.map((workflow) => (
                  <option key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </option>
                ))}
              </select>
              {selectedWorkflow ? <p className={`text-[11px] ${muted}`}>{selectedWorkflow.description}</p> : null}
            </div>
          ) : null}

          {form.sourceKind === 'knowledge_base' ? (
            <div className={`${well} mt-4`}>
              <label className={labelClass(isLight)}>Knowledge base</label>
              <select
                className={fieldClass(isLight)}
                value={form.sourceId}
                onChange={(e) => {
                  const kb = knowledgeBases.find((item) => item.id === e.target.value);
                  applyListedSource(e.target.value, kb?.name || '');
                }}
              >
                <option value="">Select a knowledge base</option>
                {knowledgeBases.map((kb) => (
                  <option key={kb.id} value={kb.id}>
                    {kb.name}
                    {kb.documentCount != null ? ` · ${kb.documentCount} docs` : ''}
                  </option>
                ))}
              </select>
              {!knowledgeBases.length ? <p className={`text-xs ${muted}`}>No knowledge bases are available for this tenant.</p> : null}
            </div>
          ) : null}

          {form.sourceKind === 'external_mcp' ? (
            <div className={`${well} mt-4`}>
              <div>
                <label className={labelClass(isLight)}>External MCP</label>
                <select
                  className={fieldClass(isLight)}
                  value={form.sourceId}
                  onChange={(e) => {
                    const connection = externalMcp.find((item) => item.id === e.target.value);
                    applyListedSource(e.target.value, connection?.name || '');
                  }}
                >
                  <option value="">Select a connected MCP</option>
                  {externalMcp.map((connection) => (
                    <option key={connection.id} value={connection.id}>
                      {connection.name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedExternal ? (
                <div className="grid md:grid-cols-2 gap-2">
                  {selectedExternal.tools.map((tool) => (
                    <Chip
                      key={tool.name}
                      isLight={isLight}
                      selected={form.selectedExistingToolNames.includes(tool.name)}
                      label={tool.name}
                      hint={tool.description}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          selectedExistingToolNames: [tool.name],
                          inputSchema: prettyJson(tool.inputSchema) || tool.inputSchema,
                        }))
                      }
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {form.sourceKind === 'custom_text' ? (
            <div className={`${well} mt-4`}>
              <MaximizableTextarea
                label="Custom resource"
                value={form.customResourceText}
                onChange={(customResourceText) => setForm((p) => ({ ...p, customResourceText }))}
                placeholder="Describe the resource URI, shape, and how agents should read it."
                isLight={isLight}
                rows={8}
                mono
              />
            </div>
          ) : null}
        </Section>

        <div
          className={`rounded-2xl border overflow-hidden ${
            isLight ? 'bg-white border-neutral-200/90 shadow-[0_1px_2px_rgba(16,24,40,0.04)]' : 'bg-neutral-900/70 border-neutral-800'
          }`}
        >
          <button
            type="button"
            className={`w-full flex items-center justify-between px-5 sm:px-6 py-4 text-left ${isLight ? 'hover:bg-neutral-50/80' : 'hover:bg-neutral-800/40'}`}
            onClick={() => {
              setAdvancedOpen((value) => !value);
              logGovernedMcp('app', 'publish_advanced_toggled', { open: !advancedOpen });
            }}
            aria-expanded={advancedOpen}
          >
            <span>
              <span className={`block text-[10px] font-semibold uppercase tracking-[0.16em] mb-1 ${isLight ? 'text-indigo-500' : 'text-indigo-300'}`}>
                04
              </span>
              <span className={`block text-[15px] font-semibold tracking-tight ${title}`}>Advanced</span>
              <span className={`block text-sm mt-1 ${muted}`}>Guidance, schemas, extra groups, and risk controls</span>
            </span>
            <span className={`h-8 w-8 rounded-full border flex items-center justify-center shrink-0 ${isLight ? 'border-neutral-200' : 'border-neutral-700'}`}>
              <ChevronDown size={16} className={`transition-transform ${advancedOpen ? 'rotate-180' : ''} ${muted}`} />
            </span>
          </button>
          {advancedOpen ? (
            <div className={`px-5 sm:px-6 pb-6 space-y-6 border-t ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
              <div className="pt-5 grid md:grid-cols-[160px_minmax(0,1fr)] gap-4">
                <div>
                  <label className={labelClass(isLight)}>Version</label>
                  <input className={fieldClass(isLight)} value={form.version} onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))} />
                </div>
                <div>
                  <label className={labelClass(isLight)}>Guidance</label>
                  <textarea
                    className={fieldClass(isLight)}
                    rows={2}
                    value={form.usageGuidance}
                    onChange={(e) => setForm((p) => ({ ...p, usageGuidance: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass(isLight)}>Use when</label>
                  <textarea className={fieldClass(isLight)} rows={3} value={form.useWhen} onChange={(e) => setForm((p) => ({ ...p, useWhen: e.target.value }))} />
                </div>
                <div>
                  <label className={labelClass(isLight)}>Do not use when</label>
                  <textarea
                    className={fieldClass(isLight)}
                    rows={3}
                    value={form.doNotUseWhen}
                    onChange={(e) => setForm((p) => ({ ...p, doNotUseWhen: e.target.value }))}
                  />
                </div>
              </div>
              {form.mcpCapability === 'tool' ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <MaximizableTextarea
                    label="Input schema"
                    value={form.inputSchema}
                    onChange={(inputSchema) => setForm((p) => ({ ...p, inputSchema }))}
                    isLight={isLight}
                    rows={10}
                    mono
                    copilot
                    onCopilot={() => {
                      logGovernedMcp('app', 'publish_schema_copilot_clicked', { side: 'input' });
                      setError(null);
                    }}
                  />
                  <MaximizableTextarea
                    label="Output schema"
                    value={form.outputSchema}
                    onChange={(outputSchema) => setForm((p) => ({ ...p, outputSchema }))}
                    isLight={isLight}
                    rows={10}
                    mono
                    copilot
                    onCopilot={() => {
                      logGovernedMcp('app', 'publish_schema_copilot_clicked', { side: 'output' });
                      setError(null);
                    }}
                  />
                </div>
              ) : null}
              <MultiSelectDropdown
                label="Groups"
                values={capabilityGroupIds(form)}
                options={toolGroups.map((group) => ({
                  id: group.id,
                  label: group.name,
                  hint: group.description,
                  locked: group.id === primaryGroupId,
                }))}
                onChange={(nextIds) => {
                  const groupIds = mergeGroupIds(primaryGroupId, nextIds);
                  setForm((p) => ({ ...p, groupIds, groupId: primaryGroupId || groupIds[0] || '' }));
                  logGovernedMcp('app', 'publish_identity_groups_changed', { groupIds });
                }}
                isLight={isLight}
                placeholder="Additional groups"
              />

              <div className={`rounded-xl border p-4 space-y-4 ${isLight ? 'border-neutral-200 bg-neutral-50/70' : 'border-neutral-800 bg-neutral-950/50'}`}>
                <div className="flex items-center gap-2">
                  <Shield size={15} className={isLight ? 'text-indigo-500' : 'text-indigo-300'} />
                  <h3 className={`text-sm font-semibold ${title}`}>Risk</h3>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass(isLight)}>Risk</label>
                    <select
                      className={fieldClass(isLight)}
                      value={form.riskLevel}
                      onChange={(e) => setForm((p) => ({ ...p, riskLevel: e.target.value as PublishedCapability['riskLevel'] }))}
                    >
                      {RISK_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass(isLight)}>Rate limit / min</label>
                    <input
                      type="number"
                      className={fieldClass(isLight)}
                      value={form.rateLimitPerMinute}
                      onChange={(e) => setForm((p) => ({ ...p, rateLimitPerMinute: Number(e.target.value) || 0 }))}
                    />
                  </div>
                  <div>
                    <label className={labelClass(isLight)}>Max records</label>
                    <input
                      type="number"
                      className={fieldClass(isLight)}
                      value={form.maxRecords}
                      onChange={(e) => setForm((p) => ({ ...p, maxRecords: Number(e.target.value) || 0 }))}
                    />
                  </div>
                </div>
                <div className={`rounded-xl border px-4 divide-y ${isLight ? 'border-neutral-200 bg-white divide-neutral-100' : 'border-neutral-800 bg-neutral-900 divide-neutral-800'}`}>
                  <ToggleRow
                    label="Prompt-injection protection"
                    checked={form.promptInjectionProtection}
                    onChange={(promptInjectionProtection) => setForm((p) => ({ ...p, promptInjectionProtection }))}
                    isLight={isLight}
                  />
                  <ToggleRow
                    label="PII detection"
                    checked={form.piiDetection}
                    onChange={(piiDetection) => setForm((p) => ({ ...p, piiDetection }))}
                    isLight={isLight}
                  />
                  <ToggleRow
                    label="Secrets / credential leakage"
                    checked={form.secretsLeakageProtection}
                    onChange={(secretsLeakageProtection) => setForm((p) => ({ ...p, secretsLeakageProtection }))}
                    isLight={isLight}
                  />
                  <ToggleRow
                    label="Jailbreak / instruction-override"
                    checked={form.jailbreakProtection}
                    onChange={(jailbreakProtection) => setForm((p) => ({ ...p, jailbreakProtection }))}
                    isLight={isLight}
                  />
                  <ToggleRow
                    label="Output governance"
                    checked={form.outputGovernance}
                    onChange={(outputGovernance) => setForm((p) => ({ ...p, outputGovernance }))}
                    isLight={isLight}
                  />
                  <ToggleRow
                    label="Mask / redact sensitive fields"
                    checked={form.maskingEnabled}
                    onChange={(maskingEnabled) => setForm((p) => ({ ...p, maskingEnabled }))}
                    isLight={isLight}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </GatewayDrawer>
    {showIngestionModal
      ? createPortal(
          <IngestionEntityModal
            overlayClassName="z-[200]"
            onClose={() => {
              setShowIngestionModal(false);
              setCreateError(null);
              logGovernedMcp('app', 'publish_add_data_model_dismissed');
            }}
            onSubmit={handleCreateDataModel}
            submitting={creatingModel}
            submitError={createError}
          />,
          document.body
        )
      : null}
    </>
  );
}
